// Payments error-code registry — machine-readable codes emitted ALONGSIDE the
// human-readable `error` string on the money routes (paypal-orders/create +
// capture). The contract is purely additive: old clients keep reading `error`
// (or `message`), new clients branch on `code`. A frozen mobile build may pin
// any of these forever, so codes are SCREAMING_SNAKE_CASE, documented, and
// NEVER recycled to a new meaning.
//
// @see .tondocs/payments/API_CONTRACT_HARDENING.md  C2 (the registry + rationale)
// @see lib/api/error.ts  the central apiError() ApiErrorCode union used by
//   seller-provider etc.; the overlapping codes here (UNAUTHORIZED, FORBIDDEN,
//   INTERNAL_ERROR) are intentionally mirrored — these two routes emit raw
//   additive bodies rather than the apiError shape, to preserve `error` verbatim.

import type { RefundErrorCode } from "@kraahl/contract";

export const PAYMENTS_ERROR_CODES = {
  // shared with lib/api/error.ts (kept in sync intentionally)
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  INTERNAL_ERROR: "INTERNAL_ERROR",

  // create route
  INVALID_PURCHASE_UNITS: "INVALID_PURCHASE_UNITS",
  // Structural shape miss on the new `CheckoutIntentItem.context` discriminator
  // (missing field, unknown kind, offer kind without offerId). Emitted by the
  // route's isCheckoutIntent guard BEFORE any DB roundtrip. Distinct from
  // PAYMENT_BREAKDOWN_MISMATCH (422), which is "shape is valid but DB rejects
  // pricing". @see .tondocs/payments/INTENT_CONTEXT_KIND.md
  INVALID_INTENT_SHAPE: "INVALID_INTENT_SHAPE",
  // Resolver rejection: intent.sellerId did not match listings[].seller_id for
  // at least one item. Distinct from INVALID_INTENT_SHAPE (boundary parse) and
  // PAYMENT_BREAKDOWN_MISMATCH (priced shape but DB-stale): the body is
  // structurally fine but semantically inconsistent. Surfaces as 400 because
  // it's a client bug (intent built wrong), not a stale-cart condition.
  // @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
  INTENT_SELLER_MISMATCH: "INTENT_SELLER_MISMATCH",
  // Structural invariant — single-seller-per-checkout is enforced in the type
  // system (see SINGLE_SELLER_INVARIANT_SPEC.md). The typed pipeline cannot
  // emit a multi-seller `purchase_units[]`; this code survives ONLY as a
  // raw-HTTP / future-regression defence-in-depth backstop on the route.
  MULTI_SELLER_NOT_SUPPORTED: "MULTI_SELLER_NOT_SUPPORTED",
  INVALID_PAYMENT_SOURCE: "INVALID_PAYMENT_SOURCE",
  PAYPAL_CREATE_FAILED: "PAYPAL_CREATE_FAILED",

  // §O.2.2 — builder-side guard. PayPal hard-rejects orders where any
  // `purchase_unit.payment_instruction.platform_fees[].amount` exceeds the
  // unit's `amount.value` (sandbox-verified, §O.2.1 SHA 815b775a). Without
  // the builder guard, the buyer sees a generic PayPal failure and the cart
  // clears. Caught at our boundary with a machine-readable code; the
  // trip-wire event (§M.11) fires from the producer side.
  // @see .tondocs/shippo/PLAN.md §O.2.2 + §M.11
  // @see lib/payments/build-paypal-purchase-units.ts (PaypalPlatformFeeCapExceededError)
  PAYPAL_PLATFORM_FEE_CAP_EXCEEDED: "PAYPAL_PLATFORM_FEE_CAP_EXCEEDED",

  // Structural rejection at the request-body envelope. Used by routes that
  // boundary-parse the full body with a Zod schema from `@kraahl/contract`
  // (capture, create non-intent fields, etc.) — distinct from
  // INVALID_INTENT_SHAPE which is the intent-only sub-parse code. Emitted on
  // safeParse failure BEFORE any business logic runs.
  // @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md
  INVALID_REQUEST_BODY: "INVALID_REQUEST_BODY",

  // capture route
  MISSING_ORDER_ID: "MISSING_ORDER_ID",
  PAYMENT_DEADLINE_EXPIRED: "PAYMENT_DEADLINE_EXPIRED",
  RESERVE_NOT_MET: "RESERVE_NOT_MET",
  DUPLICATE_ORDER: "DUPLICATE_ORDER",
  CAPTURE_FAILED: "CAPTURE_FAILED",
  ORDER_CREATION_FAILED: "ORDER_CREATION_FAILED",

  // Interim pre-capture guard for review B3 orphan-auction class
  // (.tondocs/listings/REVIEW_DISCRIMINATOR_HYGIENE_2026_07_02.md B3).
  // A listing carrying `listing_format='auction'` MUST have a sibling
  // `listing_auctions` row (the DB invariant is enforced going forward by
  // the deferred constraint trigger from migration
  // 20260702140000_create_listing_with_auction.sql, and by the atomic RPC
  // for all new writes). This code covers the tail:
  //   * pre-migration orphans that may still exist on prod
  //   * any future direct-SQL write that bypasses the RPC + trigger
  // Emitted BEFORE `captureOrder()` runs so no money moves. HTTP 409
  // (state of resource conflicts with the request — the listing exists
  // but is in an invariant-violating shape).
  AUCTION_DATA_MISSING: "AUCTION_DATA_MISSING",

  // Capture-route boundary parse of `checkoutData` against the canonical
  // {@link CheckoutPayloadSchema} discriminated union. Distinct from
  // INVALID_REQUEST_BODY (the OUTER envelope parse via
  // CaptureOrderRequestSchema): the envelope passed, but the nested
  // `checkoutData` does not satisfy the CAPTURE-channel shape (missing
  // `kind`, `live_rate` without `externalShipping`, etc.). 422 because the
  // JSON is parsable but semantically rejected at the type boundary —
  // mirrors the project's 422 convention for "shape valid, contents reject"
  // on money write surfaces. Closes the "union exists in type-land only"
  // gap — see HANDOFF_PAYLOAD_UNION_DOWNSTREAM_2026_06_27.md PR 2.
  // @see packages/contract/src/checkout-payload.ts CheckoutPayloadSchema
  INVALID_CAPTURE_PAYLOAD_SHAPE: "INVALID_CAPTURE_PAYLOAD_SHAPE",

  // Full A — emitted by the create route when a posted intent can't be
  // priced against the DB (stale listingId, removed/banned shippingMethodId,
  // expired offerId, stock/price drift between cart and create). Reuses the
  // code originally reserved for the dropped A-lite plan.
  // @see .tondocs/payments/MONEY_OWNERSHIP_ROADMAP.md Full A
  // @see lib/payments/checkout-intent.ts IntentResolutionResult
  PAYMENT_BREAKDOWN_MISMATCH: "PAYMENT_BREAKDOWN_MISMATCH",

  // Structural invariant — sibling of MULTI_SELLER_NOT_SUPPORTED. With the
  // single-seller invariant in types, the typed pipeline cannot emit a
  // multi-PU PayPal order with a wallet payment_source. This code survives
  // ONLY as a raw-HTTP defence-in-depth backstop.
  // @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
  // @see .tondocs/paypal/DIGITAL_WALLETS.md
  DIGITAL_WALLET_MULTI_SELLER: "DIGITAL_WALLET_MULTI_SELLER",

  // REFUND_V2 Phase 3 goodwill-refund endpoint (`POST /api/orders/refund`).
  // Codes are mirrored from `@kraahl/contract`'s REFUND_ERROR_CODES tuple so
  // routes emitting via `apiLegacyError` can pass them through the central
  // registry. Keep in sync: adding here without adding to the contract tuple
  // (or vice-versa) is a wire-contract drift. UI + mobile branch on `code`;
  // rename policy = never rename, never recycle.
  // @see .tondocs/payments/REFUND_V2.md §5.1
  // @see packages/contract/src/refund.ts REFUND_ERROR_CODES

  // Route rejects: `refund_attempts` unique-violation on the (seller_order_id,
  // idempotency_key) pair. Returns 200 with the existing row (idempotency-
  // correct per RFC 7807 / REFUND_V2 §5.1). Code exists for log clarity.
  ALREADY_REFUNDED: "ALREADY_REFUNDED",
  // Route rejects: currency in items[] or explicit hint disagrees with
  // `seller_orders.currency`. 422 — shape valid, contents reject.
  CURRENCY_MISMATCH: "CURRENCY_MISMATCH",
  // Route rejects: open PayPal dispute locks the capture (§7.4). 409.
  DISPUTE_OPEN: "DISPUTE_OPEN",
  // Route rejects: cumulative refund would exceed captured (DB trigger 23514,
  // §3.6 + §7.7). 422 — buyer-facing budget exceeded.
  EXCEEDS_REFUNDABLE: "EXCEEDS_REFUNDABLE",
  // Route rejects: `seller_orders.cancel_in_progress` CAS held by a
  // concurrent cancel-item flow. 423.
  LOCKED: "LOCKED",
  // Route rejects: pre-unified-fee order with no `provider_capture_id`
  // (§7.8). 422 — surface manual-refund runbook.
  PROVIDER_LEGACY_NO_CAPTURE: "PROVIDER_LEGACY_NO_CAPTURE",
  // Route rejects: Q8 (§10) — flat-rate shipping component locked once label
  // is purchased+scanned (states B-scanned/C/D per shippo PLAN §H). 422.
  SHIPPING_NOT_REFUNDABLE_AFTER_SHIP: "SHIPPING_NOT_REFUNDABLE_AFTER_SHIP",
  // Route rejects: Q8-b (§10, 2026-07-10) — shippo/live-rate goodwill NEVER
  // refunds the shipping component, independent of label state. Motivation:
  // no cross-check between `POST /orders/refund` and the label-print button,
  // so a shipping-goodwill + later label-print leaves Kraahl out-of-pocket
  // for the label cost. Cancel-path is not affected (it holds the void
  // guarantee in the same transaction). 422.
  SHIPPING_NOT_REFUNDABLE_LIVE_RATE: "SHIPPING_NOT_REFUNDABLE_LIVE_RATE",

  // PayPal rejected the order because the seller's merchant account can't
  // receive this payment. Buyer's instrument is fine. Includes
  // PAYEE_ACCOUNT_RESTRICTED / PAYEE_BLOCKED_TRANSACTION /
  // PAYEE_ACCOUNT_LOCKED_OR_CLOSED / PAYEE_NOT_ENABLED_FOR_CARD_PROCESSING /
  // PAYEE_NOT_CONSENTED / PAYEE_ACCOUNT_NOT_VERIFIED /
  // RECEIVING_LIMIT_EXCEEDED / TRANSACTION_LIMIT_EXCEEDED /
  // PAYEE_ACCOUNT_INVALID.
  //
  // Emits HTTP 409 from the create route (state of seller's resource
  // conflicts with the request). Distinct from PAYPAL_CREATE_FAILED (500 —
  // unknown PayPal failure) so the UI can show "this isn't your card or
  // account, contact the seller" instead of "Payment failed. Please try
  // again." which led buyers into the MAX_RETRIES → "contact support" trap
  // (drop incident 2026-06-26, listing 9c28998a-…, 30+ buyers in 12min).
  //
  // The canonical issue list lives in {@link SELLER_RESTRICTION_PAYPAL_ISSUES}
  // and is frozen by `tests/unit/fitness-seller-restriction-classification.test.ts`
  // — adding or removing a value MUST be an explicit decision through that
  // test, never a silent edit.
  //
  // @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T1
  // @see lib/payments/error-codes.ts PaymentCreateError
  SELLER_PAYPAL_RESTRICTED: "SELLER_PAYPAL_RESTRICTED",
} as const;

export type PaymentsErrorCode =
  (typeof PAYMENTS_ERROR_CODES)[keyof typeof PAYMENTS_ERROR_CODES];

// -----------------------------------------------------------------------------
// REFUND_V2 contract-registry link (report F10 cleanup).
//
// The Phase-3 registry above mirrors the `REFUND_ERROR_CODES` tuple exported
// from `@kraahl/contract`. Manual synchronisation drifted historically (once
// a code lived here without a wire counterpart, on the checkout side); pin
// it at the type level so an add / rename / removal on either side breaks
// tsc at this call site.
//
// The mapping records the exact `RefundErrorCode` string every registry
// entry is expected to equal; TS's `satisfies` widens on the value side so
// renames on either side fail with the concrete diff in the error message.
// -----------------------------------------------------------------------------

const _refundCodesFromRegistry = {
  ALREADY_REFUNDED: PAYMENTS_ERROR_CODES.ALREADY_REFUNDED,
  CURRENCY_MISMATCH: PAYMENTS_ERROR_CODES.CURRENCY_MISMATCH,
  DISPUTE_OPEN: PAYMENTS_ERROR_CODES.DISPUTE_OPEN,
  EXCEEDS_REFUNDABLE: PAYMENTS_ERROR_CODES.EXCEEDS_REFUNDABLE,
  LOCKED: PAYMENTS_ERROR_CODES.LOCKED,
  PROVIDER_LEGACY_NO_CAPTURE: PAYMENTS_ERROR_CODES.PROVIDER_LEGACY_NO_CAPTURE,
  SHIPPING_NOT_REFUNDABLE_AFTER_SHIP: PAYMENTS_ERROR_CODES.SHIPPING_NOT_REFUNDABLE_AFTER_SHIP,
  SHIPPING_NOT_REFUNDABLE_LIVE_RATE: PAYMENTS_ERROR_CODES.SHIPPING_NOT_REFUNDABLE_LIVE_RATE,
  // NOTE: NOT_AUTHORIZED / PROVIDER_ERROR / INVALID_REQUEST / SELLER_ORDER_NOT_FOUND
  // live in the contract union but are emitted by the route through other
  // registries (`lib/api/error.ts` ApiErrorCode / the route body directly).
  // They do NOT need a registry entry here — the wire codes still come from
  // `@kraahl/contract`.
} as const satisfies Partial<Record<RefundErrorCode, string>>;

// Silence "declared but never read" — the `satisfies` above is the pin. The
// value is not consumed elsewhere; TypeScript needs `void` (or re-export) so
// the const isn't pruned when tsc runs with unusedLocals.
void _refundCodesFromRegistry;

/**
 * PayPal issue strings (`details[0].issue` on the wire) that indicate the
 * SELLER's merchant account cannot receive the payment — KYC, capability
 * holds, receiving limits, etc. The BUYER's instrument is fine.
 *
 * Frozen list — `tests/unit/fitness-seller-restriction-classification.test.ts`
 * pins the exact set so adding/removing a value is an explicit decision in
 * the same commit (and not a silent edit that drops a real seller-side
 * symptom into the buyer-blame bucket).
 *
 * Authoring policy when extending: confirm the issue is structurally a
 * seller-side problem (PayPal Partner Dashboard "Issue" docs categorise),
 * add it both here AND in the fitness probe, and add an entry to the doc
 * comment on {@link PAYMENTS_ERROR_CODES.SELLER_PAYPAL_RESTRICTED}.
 *
 * @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T1
 */
export const SELLER_RESTRICTION_PAYPAL_ISSUES = [
  "PAYEE_ACCOUNT_RESTRICTED",
  "PAYEE_BLOCKED_TRANSACTION",
  "PAYEE_ACCOUNT_LOCKED_OR_CLOSED",
  "PAYEE_NOT_ENABLED_FOR_CARD_PROCESSING",
  "PAYEE_NOT_CONSENTED",
  "PAYEE_ACCOUNT_NOT_VERIFIED",
  "RECEIVING_LIMIT_EXCEEDED",
  "TRANSACTION_LIMIT_EXCEEDED",
  "PAYEE_ACCOUNT_INVALID",
] as const;

export type SellerRestrictionPayPalIssue =
  (typeof SELLER_RESTRICTION_PAYPAL_ISSUES)[number];

/**
 * Classifier the create route + tests share. Single source of truth for
 * "is this PayPal issue string a seller-restriction case".
 */
export function isSellerRestrictionIssue(
  issue: string | undefined | null,
): issue is SellerRestrictionPayPalIssue {
  if (!issue) return false;
  return (SELLER_RESTRICTION_PAYPAL_ISSUES as readonly string[]).includes(issue);
}

/**
 * Typed error thrown by `createPayPalOrder` in `lib/payments/checkout-actions.ts`
 * when the server returns a non-2xx from `POST /api/paypal-orders/create`.
 * Carries the machine-readable `code` from {@link PAYMENTS_ERROR_CODES} and
 * the original PayPal `details[0].issue` (when surfaced server-side) so
 * the component switches on `code` instead of regex-matching `err.message`.
 *
 * Replaces the legacy `throw new Error('Create: ${details} (debug: ${id})')`
 * pattern. Existing telemetry stays: `message` is the same human string the
 * legacy throw used, so console.error log lines remain greppable.
 *
 * @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T1
 * @see lib/payments/checkout-actions.ts createPayPalOrder
 * @see components/cart/paypal-checkout.tsx classifyPaymentError
 */
export class PaymentCreateError extends Error {
  readonly code: PaymentsErrorCode;
  readonly paypalIssue?: string;
  readonly debugId?: string;
  constructor(args: {
    code: PaymentsErrorCode;
    paypalIssue?: string;
    debugId?: string;
    message: string;
  }) {
    super(args.message);
    this.name = "PaymentCreateError";
    this.code = args.code;
    this.paypalIssue = args.paypalIssue;
    this.debugId = args.debugId;
  }
}
